import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Loader, AlertCircle, Brain, CheckCircle, FileText, Sparkles, Zap } from 'lucide-react';
import readXlsxFile from 'read-excel-file';
import { useStore } from '../store';
import { useSurveyData } from '../hooks/useSurveyData';
import { supabase } from '../lib/supabase';
import { ColumnSelector } from './ColumnSelector';
import { ColumnDetector } from '../utils/columnDetection';

export const FileUpload: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingData, setPendingData] = useState<{
    data: any[];
    headers: string[];
  } | null>(null);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [autoDetectedColumn, setAutoDetectedColumn] = useState<string | null>(null);
  const { setFileData, setWorkshopColumn, setAxes, setCurrentPage } = useStore();
  const { saveSurveyData } = useSurveyData();

  // دالة إنهاء معالجة الملف
  const finalizeFileProcessing = async (data: any[], selectedWorkshopColumn: string, headers: string[]) => {
    try {
      console.log('📁 بدء معالجة الملف:', {
        dataLength: data.length,
        workshopColumn: selectedWorkshopColumn,
        headers: headers.length
      });

      // Check workshop column validity
      if (!selectedWorkshopColumn || !headers.includes(selectedWorkshopColumn)) {
        throw new Error('عمود ورشة العمل المحدد غير صحيح');
      }

      // Extract unique workshops and log them
      const workshops = [...new Set(data.map(row => row[selectedWorkshopColumn]).filter(w => w != null && w !== ''))];
      console.log('🏛️ ورش العمل المكتشفة:', workshops);
      
      if (workshops.length === 0) {
        throw new Error('لا توجد ورش عمل في العمود المحدد');
      }

      // Save to database first
      await saveSurveyData(data, selectedWorkshopColumn);

      // Then update local state
      setFileData(data);
      setWorkshopColumn(selectedWorkshopColumn);
      
      console.log('✅ تم حفظ البيانات في Store:', {
        dataRows: data.length,
        workshopColumn: selectedWorkshopColumn,
        uniqueWorkshops: workshops.length
      });
      
      // استخدام النظام الذكي لتجميع الأسئلة
      const questionColumns = headers.filter(h => h !== selectedWorkshopColumn);
      const smartAxes = ColumnDetector.groupQuestionsIntoAxes(questionColumns);

      setAxes(smartAxes);

      // إخفاء واجهة اختيار العمود
      setShowColumnSelector(false);
      setPendingData(null);
      setError(null);

      // Navigate to axis management
      setCurrentPage('axis_management');
    } catch (error) {
      console.error('❌ خطأ في معالجة الملف:', error);
      setError(error instanceof Error ? error.message : 'حدث خطأ أثناء حفظ البيانات');
    }
  };

  // دالة التعامل مع اختيار العمود
  const handleColumnSelect = async (selectedColumn: string) => {
    if (pendingData) {
      setLoading(true);
      await finalizeFileProcessing(pendingData.data, selectedColumn, pendingData.headers);
      setLoading(false);
    }
  };

  // دالة إلغاء اختيار العمود
  const handleColumnCancel = () => {
    setShowColumnSelector(false);
    setPendingData(null);
    setError('تم إلغاء رفع الملف. يرجى المحاولة مرة أخرى.');
  };

  const validateExcelData = (rows: any[][]): boolean => {
    if (!rows || rows.length < 2) {
      throw new Error('الملف فارغ أو لا يحتوي على بيانات كافية');
    }

    // Validate headers exist and are strings
    const headers = rows[0];
    if (!headers.every(header => header && typeof header === 'string')) {
      throw new Error('تنسيق العناوين غير صحيح. يجب أن تكون جميع العناوين نصية');
    }

    // Validate data rows
    for (let i = 1; i < rows.length; i++) {
      if (rows[i].length !== headers.length) {
        throw new Error(`الصف ${i + 1} يحتوي على عدد مختلف من الأعمدة`);
      }
    }

    return true;
  };

  const processFile = async (file: File) => {
    try {
      // Check authentication first
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('يجب تسجيل الدخول أولاً لتحميل الملف');
      }

      setLoading(true);
      setError(null);

      // Validate file size
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('حجم الملف كبير جداً. يجب أن يكون أقل من 10 ميجابايت');
      }

      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (!validTypes.includes(file.type)) {
        throw new Error('نوع الملف غير صحيح. يجب أن يكون ملف Excel (.xlsx)');
      }

      // Read Excel file with enhanced error handling
      let rows: any[][];
      try {
        rows = await readXlsxFile(file) as any[][];
        // Remove any empty rows
        rows = rows.filter((row: any[]) => row.some((cell: any) => cell !== null && cell !== ''));
      } catch (e) {
        console.error('Excel reading error:', e);
        throw new Error('فشل قراءة الملف. تأكد من أن الملف بتنسيق Excel صحيح (.xlsx) وأنه لا يحتوي على وحدات ماكرو أو تنسيقات معقدة');
      }

      if (!rows || rows.length === 0) {
        throw new Error('الملف فارغ أو لا يمكن قراءته');
      }

      // Validate Excel data structure
      validateExcelData(rows);
      
      // Get headers and prepare data
      const headers = rows[0].map(String);
      
      // Convert rows to array of objects with type checking
      const processedData = rows.slice(1).map((row, rowIndex) => {
        const obj: Record<string, any> = {};
        headers.forEach((header, index) => {
          if (row[index] === undefined || row[index] === null) {
            console.warn(`Warning: Empty cell found at row ${rowIndex + 2}, column ${header}`);
            obj[header] = '';
          } else {
            obj[header] = row[index];
          }
        });
        return obj;
      });

      // استخدام النظام الذكي لكشف الأعمدة
      const detectionResult = ColumnDetector.detectColumns(headers, processedData.slice(0, 10));
      const bestSuggestion = ColumnDetector.suggestBestWorkshopColumn(detectionResult);

      console.log('🧠 نتائج الكشف الذكي:', {
        workshopColumns: detectionResult.workshopColumns.length,
        bestSuggestion: bestSuggestion?.columnName,
        confidence: bestSuggestion?.confidence,
        headers: headers
      });

      // عرض واجهة الاختيار دائماً لإعطاء المستخدم السيطرة الكاملة
      console.log('🎛️ عرض واجهة اختيار العمود للمستخدم');
      console.log('📋 جميع الأعمدة المتاحة:', headers);
      
      // تجربة تحليل سريع لكل عمود لمساعدة المستخدم
      headers.forEach(header => {
        const uniqueValues = [...new Set(processedData.slice(0, 10).map(row => row[header]).filter(v => v != null && v !== ''))];
        console.log(`📊 العمود "${header}": ${uniqueValues.length} قيم فريدة من ${Math.min(10, processedData.length)} صفوف`, uniqueValues.slice(0, 3));
      });
      
      // تعيين الاقتراح التلقائي إذا وجد
      if (bestSuggestion && bestSuggestion.confidence >= 0.3) {
        console.log('💡 اقتراح تلقائي:', bestSuggestion.columnName, 'بثقة:', bestSuggestion.confidence);
        setAutoDetectedColumn(bestSuggestion.columnName);
      }
      
      setPendingData({ data: processedData, headers });
      setShowColumnSelector(true);

    } catch (error) {
      console.error('Error processing file:', error);
      setError(error instanceof Error ? error.message : 'حدث خطأ أثناء معالجة الملف');
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (files) => {
      if (files[0]) {
        await processFile(files[0]);
      }
    },
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" dir="rtl">
      {/* Header Section */}
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold mb-4 gradient-text">
          تحليل استبانة ورشة العمل 📊
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          نظام تحليل متقدم مع ذكاء اصطناعي لكشف الأعمدة وتجميع الأسئلة تلقائياً
        </p>
      </div>

      {/* Main Upload Area */}
      <div className="max-w-4xl mx-auto">
        <div className="modern-card p-8 mb-8">
          <div
            {...getRootProps()}
            className={`
              relative border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-300
              ${isDragActive 
                ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-100 scale-105' 
                : 'border-gray-300 hover:border-blue-400 hover:bg-gradient-to-br hover:from-blue-50 hover:to-indigo-50'
              }
              ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-102'}
            `}
          >
            <input {...getInputProps()} disabled={loading} />
            
            {loading ? (
              <div className="flex flex-col items-center gap-6">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center animate-pulse-glow">
                    <Loader className="w-10 h-10 text-white animate-spin" />
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-xl font-semibold text-gray-800 mb-2">جاري تحليل البيانات...</p>
                  <p className="text-gray-600">يتم معالجة الملف باستخدام الذكاء الاصطناعي</p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Upload Icon */}
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-4 animate-float">
                  <Upload className="w-10 h-10 text-white" />
                </div>
                
                {/* Upload Text */}
                <div>
                  <p className="text-2xl font-semibold text-gray-800 mb-2">
                    {isDragActive ? 'أفلت الملف هنا ✨' : 'اسحب وأفلت ملف Excel هنا'}
                  </p>
                  <p className="text-gray-600 text-lg">
                    أو انقر لاختيار الملف (.xlsx فقط)
                  </p>
                </div>

                {/* Features Highlight */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50">
                    <Brain className="w-6 h-6 text-blue-500" />
                    <span className="text-sm font-medium text-gray-700">كشف ذكي للأعمدة</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50">
                    <Sparkles className="w-6 h-6 text-purple-500" />
                    <span className="text-sm font-medium text-gray-700">تجميع تلقائي للأسئلة</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50">
                    <Zap className="w-6 h-6 text-green-500" />
                    <span className="text-sm font-medium text-gray-700">معالجة فورية</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-8">
            <div className="notification-error">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* AI Suggestion Display */}
        {autoDetectedColumn && showColumnSelector && (
          <div className="mb-8">
            <div className="modern-card p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-blue-900 mb-2 flex items-center gap-2">
                    🧠 اقتراح النظام الذكي
                  </h3>
                  <p className="text-blue-800 mb-1">
                    العمود المقترح: <span className="font-bold bg-blue-200 px-2 py-1 rounded">{autoDetectedColumn}</span>
                  </p>
                  <p className="text-blue-700 text-sm">
                    يمكنك قبول الاقتراح أو اختيار عمود آخر بنفسك
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Features Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* New Features */}
          <div className="modern-card p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-500" />
              ميزات النظام الجديد
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mt-2"></div>
                <p className="text-gray-700">🧠 كشف تلقائي ذكي لعمود الورش (يدعم: ورشة، دورة، مادة، مقرر، برنامج، وغيرها)</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-green-500 to-blue-600 mt-2"></div>
                <p className="text-gray-700">📊 دعم المقاييس المتعددة (رضا، موافقة، جودة، أداء)</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-600 mt-2"></div>
                <p className="text-gray-700">🎯 اقتراحات ذكية لتجميع الأسئلة في محاور</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-500 to-red-600 mt-2"></div>
                <p className="text-gray-700">✨ واجهة اختيار متقدمة عند عدم وضوح العمود</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-teal-500 to-green-600 mt-2"></div>
                <p className="text-gray-700">🔍 تحليل محتوى البيانات لتحسين الدقة</p>
              </div>
            </div>
          </div>

          {/* File Requirements */}
          <div className="modern-card p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-500" />
              متطلبات الملف
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-gray-700">تنسيق Excel (.xlsx)</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
                <CheckCircle className="w-5 h-5 text-blue-500" />
                <span className="text-gray-700">عمود للتجميع (ورشة، دورة، مادة، إلخ...)</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50">
                <CheckCircle className="w-5 h-5 text-purple-500" />
                <span className="text-gray-700">أعمدة للأسئلة</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-orange-50 to-red-50">
                <CheckCircle className="w-5 h-5 text-orange-500" />
                <span className="text-gray-700">صفوف للمستجيبين</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-gray-50 to-slate-50">
                <CheckCircle className="w-5 h-5 text-gray-500" />
                <span className="text-gray-700">حجم الملف أقل من 10 ميجابايت</span>
              </div>
            </div>
          </div>
        </div>

        {/* Column Selector */}
        {showColumnSelector && pendingData && (
          <div className="modern-card p-8">
            <ColumnSelector
              headers={pendingData.headers}
              sampleData={pendingData.data.slice(0, 10)}
              onColumnSelect={handleColumnSelect}
              onCancel={handleColumnCancel}
              currentSelection={autoDetectedColumn || undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
};